import { sha256 } from 'js-sha256';
import { create } from 'zustand';

import {
    CSRF_LOCAL_STORAGE_KEY,
    EMAIL_LOCAL_STORAGE_KEY,
    FIRST_NAME_LOCAL_STORAGE_KEY,
    LAST_NAME_LOCAL_STORAGE_KEY,
    PHONE_LOCAL_STORAGE_KEY,
    SESSION_COOKIE_NAME,
    SUB_USER_ID_LOCAL_STORAGE_KEY,
    SUB_USER_SCOPES_LOCAL_STORAGE_KEY,
    USER_ID_LOCAL_STORAGE_KEY,
    USER_ROLE_LOCAL_STORAGE_KEY,
} from 'constants/session';
import { SESSION_COOKIE_ENDPOINT } from 'lib/session/sessionCookie';
import type { UserVo } from 'models';
import type { SubUserScope } from 'models/reqres';
import { hasBroadWriteScope, hasTradeScope, hasTransferScope } from 'models/subUserScopes';
import type { AccessLevel } from 'models/user';
import {
    getLocalStorageWithExpiration,
    setLocalStorageWithExpiration,
    removeLocalStorageItem,
    removeSessionStorageItem,
    getCookie,
    removeCookie,
    setCookieWithExpiration,
} from 'utils/storage';

export type AuthLogoutReason = 'session_expired' | 'maintenance' | 'manual' | 'unavailability';

export type ForcedDisconnectReason = Exclude<AuthLogoutReason, 'manual'>;

// 'manual' logouts (explicit sign-out) run their own teardown via UserStore.endSession();
// only the forced reasons need disconnect UI and forced session teardown.
export function isForcedDisconnectReason(reason: AuthLogoutReason): reason is ForcedDisconnectReason {
    return reason !== 'manual';
}

export function selectForcedDisconnectReason(state: Pick<AuthState, 'logoutReason'>): ForcedDisconnectReason | null {
    if (state.logoutReason === null || !isForcedDisconnectReason(state.logoutReason)) {
        return null;
    }
    return state.logoutReason;
}

const emptyUser: UserVo = {
    user_id: '',
    wire_code: '',
    created_ts: '',
    email: '',
    first_name: '',
    last_name: '',
    birth_date: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    deposits: 0,
    use_bid_ask: false,
    country_code: '',
    area_code: '',
    phone_number: '',
    access_level: '',
    frontend_sessions: 0,
    perps_only: false,
    data_only: false,
    is_institutional: false,
};

const MEMBER_ACCESS_LEVELS: AccessLevel[] = ['member', 'surveillance', 'internal_member', 'admin'];

export const isMemberAccessLevel = (accessLevel: AccessLevel): boolean => MEMBER_ACCESS_LEVELS.includes(accessLevel);
const INTERNAL_MEMBER_ACCESS_LEVELS: AccessLevel[] = ['internal_member', 'admin'];

const getAuthBooleans = (userId: string | null, accessLevel: AccessLevel) => {
    const isLoggedIn = !!userId && !!accessLevel;

    return {
        isLoggedIn,
        isLoggedInMember: isLoggedIn && MEMBER_ACCESS_LEVELS.includes(accessLevel),
        isLoggedInInternalMember: isLoggedIn && INTERNAL_MEMBER_ACCESS_LEVELS.includes(accessLevel),
        isLoggedInAdmin: isLoggedIn && accessLevel === 'admin',
        isLoggedInApplicant: isLoggedIn && accessLevel === 'applicant',
    };
};

const getSubUserAccess = (isSubUser: boolean, scopes: SubUserScope[]) => {
    if (!isSubUser) {
        return {
            hasTradeAccess: true,
            hasTransferAccess: true,
            hasBroadWriteAccess: true,
            isSubUserReadOnly: false,
        };
    }

    const hasTradeAccess = hasTradeScope(scopes);
    const hasTransferAccess = hasTransferScope(scopes);

    return {
        hasTradeAccess,
        hasTransferAccess,
        hasBroadWriteAccess: hasBroadWriteScope(scopes),
        // Read-only means no write capability at all — not trade, transfer, or broad write.
        // (broad write implies trade, so the two child checks already cover it.)
        isSubUserReadOnly: !hasTradeAccess && !hasTransferAccess,
    };
};

export interface AuthActions {
    getCsrfToken: () => string | null;
    setCsrfToken: (token: string) => void;
    clearCsrfToken: () => void;
    login: (userId: string) => void;
    logout: (reason: AuthLogoutReason) => void;
    clearLogoutReason: () => void;
    notifyUnauthorizedResponse: () => void;
    /**
     * Clears persisted auth credentials (CSRF token, user id/role, sub-user id/scopes,
     * cookie) without touching in-memory auth state or logoutReason. Used for 401
     * responses received while there's no userId in state to log out — the persisted
     * storage can still be stale/corrupted and must not be left behind.
     */
    clearPersistedSession: () => void;
    setUserId: (userId: string | null) => void;
    setAccessLevel: (accessLevel: AccessLevel) => void;
    clearAccessLevel: () => void;
    /** Invalidates account-policy authorization while retaining the last canonical user for rendering. */
    userInvalidated: () => void;
    userUpdated: (user: UserVo) => void;
    setSubUser: (subUserId: string, scopes: SubUserScope[]) => void;
    clearSubUser: () => void;

    /**
     * initialize auth from the cookie set by userSessionStore, since this store isn't yet the source of truth for auth state
     * TODO(jihok): once zustand store is full owner of the auth state, let persist middleware handle this
     */
    _initializeFromCookie: () => void;
}

export interface AuthDerived {
    fullName: () => string;
    fullPhoneNumber: () => string;
    profile: () => UserVo;
    hasTransfersAccess: () => boolean;
}

export interface AuthState {
    userId: string | null;
    accessLevel: AccessLevel;
    user: UserVo | null;
    /**
     * Keeps the last canonical profile renderable while marking its
     * authorization policy unusable until a post-mutation profile refresh.
     */
    accountPolicyInvalidatedForUserId: string | null;
    isLoggedIn: boolean;
    isLoggedInMember: boolean;
    isLoggedInInternalMember: boolean;
    isLoggedInAdmin: boolean;
    isLoggedInApplicant: boolean;
    logoutReason: AuthLogoutReason | null;
    subUserId: string | null;
    subUserScopes: SubUserScope[];
    isSubUser: boolean;
    isSubUserReadOnly: boolean;
    hasTradeAccess: boolean;
    hasTransferAccess: boolean;
    hasBroadWriteAccess: boolean;
    canManageSubUsers: boolean;
    _hasInitialized: boolean;
    /** Bumped on CSRF token rotation — will drive ws reconnection. */
    credentialsVersion: number;
    /** Bumped on every 401 response, regardless of login state — a signal for side-effect cleanup (e.g. registration-local state) that isn't tied to a full logout. */
    unauthorizedResponseToken: number;
    derived: AuthDerived;
    actions: AuthActions;
}

export function selectPublicPollingEnabled(state: Pick<AuthState, '_hasInitialized' | 'isLoggedIn'>): boolean {
    return state._hasInitialized && !state.isLoggedIn;
}

/**
 * Expires the exchange-issued `sessions` cookie.
 *
 * Only explicit sign-out clears it server-side (`POST /log_out`); a forced disconnect
 * or a bare 401 tears the client session down without ever telling the backend, so the
 * cookie — 30 days long, scoped to the shared parent domain — outlives the session it
 * represents. `proxy.ts` reads it to choose the authenticated vs anonymous variant of
 * the home and calendar pages, so leaving it behind keeps handing signed-out visitors
 * a cached page built for a logged-in user.
 *
 * Two mechanisms because we can't assume the cookie's flags. `removeCookie` deletes it
 * synchronously — before any subsequent navigation the proxy would see — but is a
 * silent no-op if the backend marked it `HttpOnly`. The `DELETE` covers that case: a
 * `Set-Cookie` from our own origin expires it regardless of flags. `keepalive` so the
 * request survives a navigation kicked off by the same teardown.
 */
/**
 * How long the session-cookie expiry below may stay outstanding before it is abandoned.
 *
 * This bound is what limits the window in which a cleanup request can do damage. The
 * `DELETE` expires an HttpOnly cookie through the `Set-Cookie` on its *response*, so
 * one that lands long after it was sent expires whichever session is current by then —
 * a user who signed out and back in gets silently unauthenticated. Aborting caps that
 * window instead of leaving it open for as long as the request survives.
 *
 * Aborting has a cost in the other direction, which is what sets the value. This
 * endpoint is cookie-only — it expires the cookie through its response and invalidates
 * nothing server-side — so a logout whose DELETE is abandoned leaves a *valid* HttpOnly
 * session cookie in the browser, which matters on a shared device. So the two failure
 * modes are not symmetric: the one aborting prevents needs the user to sign out and
 * back in inside the window, while the one it causes lands on any slow logout. Hence a
 * bound set well outside normal same-origin latency rather than a tight one — long
 * enough that a real logout on a bad connection still completes, short enough that the
 * late-response race stays capped.
 *
 * Deliberately NOT paired with a barrier that logins wait on. That was tried (see the
 * history around this comment) and is the wrong trade: it makes every sign-in on the
 * site depend on a best-effort cleanup request, turning "occasionally sign in twice"
 * into "sign-in blocked". The residual race — a login issued inside this window — is
 * documented in the PR rather than papered over here.
 */
const SESSION_COOKIE_CLEAR_TIMEOUT_MS = 15000;

function clearSessionCookie(): void {
    removeCookie(SESSION_COOKIE_NAME);

    if (typeof window === 'undefined') return;

    // Best-effort: the synchronous delete above already covers the non-HttpOnly case,
    // and a failure here must not break sign-out.
    //
    // Bare `fetch` on purpose. `fetchWithAwsWaf` only attaches a token to the exchange
    // API host (`isProtectedApiRequest`) and passes everything else straight through, so
    // it would add nothing for a same-origin route handler — while pulling the WAF module
    // into `authStore`'s import graph, which is loaded on every route.
    //
    // Aborted on timeout: a DELETE this stale can only do harm, since its response's
    // `Set-Cookie` would expire whichever session is current by then. Aborting is
    // best-effort at stopping that — it only wins the race if it lands before the
    // response *headers* do, since the cookie is applied by the network stack whether
    // or not JS ever reads the body. The timer is JS-side, so a navigation that
    // discards this context still leaves `keepalive` to finish the request as before.
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), SESSION_COOKIE_CLEAR_TIMEOUT_MS);
    // eslint-disable-next-line no-restricted-globals
    void fetch(SESSION_COOKIE_ENDPOINT, { method: 'DELETE', keepalive: true, signal: abort.signal })
        .then(
            () => undefined,
            () => undefined,
        )
        .finally(() => clearTimeout(timeout));
}

// Shared by `logout` and `clearPersistedSession` — removes every persisted auth
// credential (localStorage + cookie), leaving in-memory state untouched.
function clearPersistedAuthStorage(): void {
    removeLocalStorageItem(CSRF_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(USER_ID_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(USER_ROLE_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(SUB_USER_ID_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(SUB_USER_SCOPES_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(EMAIL_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(PHONE_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(FIRST_NAME_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(LAST_NAME_LOCAL_STORAGE_KEY);
    removeCookie(USER_ID_LOCAL_STORAGE_KEY);
    clearSessionCookie();
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ em: '', ph: '' });
    }
}

export const useAuthStore = create<AuthState>()((set, get) => ({
    userId: null,
    accessLevel: '',
    user: null,
    accountPolicyInvalidatedForUserId: null,
    ...getAuthBooleans(null, ''),
    logoutReason: null,
    subUserId: null,
    subUserScopes: [],
    isSubUser: false,
    isSubUserReadOnly: false,
    hasTradeAccess: true,
    hasTransferAccess: true,
    hasBroadWriteAccess: true,
    canManageSubUsers: false,
    _hasInitialized: false,
    credentialsVersion: 0,
    unauthorizedResponseToken: 0,
    derived: {
        fullName: () => {
            const u = get().user;
            return u?.user_id ? `${u.first_name} ${u.last_name}`.trim() : '';
        },
        fullPhoneNumber: () => {
            const u = get().user;
            return `+${u?.country_code ?? ''}${u?.area_code ?? ''}${u?.phone_number ?? ''}`;
        },
        profile: () => get().user ?? emptyUser,
        hasTransfersAccess: () => {
            return get().hasTransferAccess;
        },
    },
    actions: {
        getCsrfToken: () => getLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY),
        setCsrfToken: (token) => {
            setLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY, token);
            set({ credentialsVersion: get().credentialsVersion + 1 });
        },
        clearCsrfToken: () => removeLocalStorageItem(CSRF_LOCAL_STORAGE_KEY),
        login: (userId) => {
            const { accessLevel, userId: previousUserId } = get();
            set({
                userId,
                accountPolicyInvalidatedForUserId:
                    previousUserId === userId ? get().accountPolicyInvalidatedForUserId : userId,
                ...getAuthBooleans(userId, accessLevel),
                logoutReason: null,
            });
        },
        logout: (reason) => {
            const userId = get().userId;
            // Clear persisted credentials (CSRF token included) before set()
            // below — set() synchronously notifies subscribers (e.g.
            // sessionTeardownEffects), and a forced disconnect's teardown
            // reopens the shared exchange socket inline. That reopen reads
            // the CSRF token from localStorage at connect time, so it must
            // already be cleared or the replacement socket re-authenticates
            // under the very session this logout is ending.
            clearPersistedAuthStorage();
            if (userId) {
                removeSessionStorageItem(`userSafety_depositGuard_shown_${userId}`);
            }
            set({
                userId: null,
                accessLevel: '',
                user: null,
                accountPolicyInvalidatedForUserId: null,
                ...getAuthBooleans(null, ''),
                logoutReason: reason,
                subUserId: null,
                subUserScopes: [],
                isSubUser: false,
                ...getSubUserAccess(false, []),
                canManageSubUsers: false,
            });
        },
        clearLogoutReason: () => set({ logoutReason: null }),
        notifyUnauthorizedResponse: () => set({ unauthorizedResponseToken: get().unauthorizedResponseToken + 1 }),
        clearPersistedSession: () => clearPersistedAuthStorage(),
        setUserId: (userId) => {
            const { accessLevel, logoutReason, userId: previousUserId } = get();
            set({
                userId,
                accountPolicyInvalidatedForUserId:
                    previousUserId === userId ? get().accountPolicyInvalidatedForUserId : userId,
                ...getAuthBooleans(userId, accessLevel),
                logoutReason: userId ? null : logoutReason,
            });
        },
        setAccessLevel: (accessLevel) => {
            const userId = get().userId;
            set({
                accessLevel,
                ...getAuthBooleans(userId, accessLevel),
            });
        },
        clearAccessLevel: () => {
            const userId = get().userId;
            set({
                accessLevel: '',
                ...getAuthBooleans(userId, ''),
            });
        },
        userInvalidated: () => set({ accountPolicyInvalidatedForUserId: get().userId }),
        userUpdated: (user) => {
            // Persist email and phone number in local storage for pre-filling forms.
            // These are not sensitive information like user ID or access level, but we still set an expiration time on them to avoid stale data.
            if (user.email) {
                setLocalStorageWithExpiration(EMAIL_LOCAL_STORAGE_KEY, user.email);
            }

            // Normalize phone number to digits only (including country code)
            const fullPhone = `${user.country_code || ''}${user.area_code || ''}${user.phone_number || ''}`;
            const normalizedPhone = fullPhone.replace(/\D/g, '');
            if (normalizedPhone) {
                setLocalStorageWithExpiration(PHONE_LOCAL_STORAGE_KEY, normalizedPhone);
            }

            if (typeof window !== 'undefined') {
                const hashedEmail = user.email ? sha256(user.email) : '';
                const hashedPhone = normalizedPhone ? sha256(normalizedPhone) : '';
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    em: hashedEmail,
                    ph: hashedPhone,
                });
            }

            // Update user info.
            const accessLevel = user.access_level || get().accessLevel;
            set({
                user,
                userId: user.user_id || null,
                accountPolicyInvalidatedForUserId: null,
                accessLevel,
                ...getAuthBooleans(user.user_id || null, accessLevel),
                canManageSubUsers: user.can_manage_sub_users ?? false,
            });
        },
        setSubUser: (subUserId, scopes) => {
            setLocalStorageWithExpiration(SUB_USER_ID_LOCAL_STORAGE_KEY, subUserId);
            setLocalStorageWithExpiration(SUB_USER_SCOPES_LOCAL_STORAGE_KEY, JSON.stringify(scopes));
            set({
                subUserId,
                subUserScopes: scopes,
                isSubUser: true,
                ...getSubUserAccess(true, scopes),
            });
        },
        clearSubUser: () => {
            removeLocalStorageItem(SUB_USER_ID_LOCAL_STORAGE_KEY);
            removeLocalStorageItem(SUB_USER_SCOPES_LOCAL_STORAGE_KEY);
            set({
                subUserId: null,
                subUserScopes: [],
                isSubUser: false,
                ...getSubUserAccess(false, []),
            });
        },
        _initializeFromCookie: () => {
            // Normalized with `||`, not `??`: an empty `userId=` cookie is a cookie that
            // carries no identity, and it reaches the same conclusion the proxy does
            // (`hasAuthenticatedSession` treats an empty value as no session). Left as an
            // empty string it would both suppress the localStorage fallback below and
            // resolve to a logged-out store while the re-mint branch writes a cookie.
            const cookieUserId = getCookie(USER_ID_LOCAL_STORAGE_KEY) || null;
            const storedUserId = (getLocalStorageWithExpiration(USER_ID_LOCAL_STORAGE_KEY) as string | null) || null;
            const userId = cookieUserId ?? storedUserId;

            /**
             * Re-mint the `userId` cookie when only localStorage survived.
             *
             * Every logout path drops both together (`clearPersistedAuthStorage`,
             * `clearUserId`), so cookie-gone-but-localStorage-present never means
             * "signed out" — it means the cookie was lost on its own (browser
             * lifetime caps on script-written cookies, a cookie-domain change,
             * per-site cookie clearing). This restore path would then start the
             * client logged in while the edge still reads the request as anonymous
             * and rewrites it to an ISR variant built for signed-out visitors
             * (`hasAuthenticatedSession` in proxy.ts). Writing the cookie back keeps
             * the two in agreement from the next request onward; `ExploreAuthRouteRefresh`
             * corrects the page that is already on screen.
             */
            if (!cookieUserId && storedUserId) {
                setCookieWithExpiration(USER_ID_LOCAL_STORAGE_KEY, storedUserId);
            }
            const accessLevel = (getLocalStorageWithExpiration(USER_ROLE_LOCAL_STORAGE_KEY) as AccessLevel) || '';
            const subUserId = getLocalStorageWithExpiration(SUB_USER_ID_LOCAL_STORAGE_KEY) || null;
            let subUserScopes: SubUserScope[] = [];
            const rawScopes = getLocalStorageWithExpiration(SUB_USER_SCOPES_LOCAL_STORAGE_KEY);
            if (rawScopes) {
                try {
                    subUserScopes = JSON.parse(rawScopes) as SubUserScope[];
                } catch {
                    subUserScopes = [];
                }
            }
            set({
                userId: userId || null,
                accountPolicyInvalidatedForUserId:
                    get().userId === (userId || null) ? get().accountPolicyInvalidatedForUserId : userId || null,
                accessLevel,
                ...getAuthBooleans(userId || null, accessLevel),
                subUserId,
                subUserScopes,
                isSubUser: !!subUserId,
                ...getSubUserAccess(!!subUserId, subUserScopes),
                _hasInitialized: true,
            });
        },
    },
}));

if (typeof window !== 'undefined') {
    useAuthStore.getState().actions._initializeFromCookie();
}
